import { useCallback } from 'react';
import { Permission } from '../types';
import { useUserSetupStore } from '../user-setup.store';

export const hasFlag = (mask: number, flag: number) => (mask & flag) === flag;

interface UseCanProps {
  permissions: Permission[] | Permission;
  operator?: 'AND' | 'OR';
}

interface HasUserPermissionsParams extends Permission {
  permissionMask: Record<string, number>;
}

const hasUserPermissions = ({
  actions,
  permissionMask,
  resources,
}: HasUserPermissionsParams) => {
  const requiredPermissions = Array.isArray(actions) ? actions : [actions];
  const requiredResources = Array.isArray(resources) ? resources : [resources];

  return requiredResources.every(resource => {
    const userPermissionMask = permissionMask[resource];

    if (!userPermissionMask) return false;

    return requiredPermissions.some(requiredPermission =>
      hasFlag(userPermissionMask, requiredPermission)
    );
  });
};

export const useCan = () => {
  const { actionPermissions: currentOrganizationPermissions } =
    useUserSetupStore();

  const can = useCallback(
    ({ permissions, operator = 'AND' }: UseCanProps) => {
      if (!currentOrganizationPermissions) return true;
      const permissionsArray = Array.isArray(permissions)
        ? permissions
        : [permissions];

      return operator === 'OR'
        ? permissionsArray.some(permission =>
            hasUserPermissions({
              ...permission,
              permissionMask: currentOrganizationPermissions,
            })
          )
        : permissionsArray.every(permission =>
            hasUserPermissions({
              ...permission,
              permissionMask: currentOrganizationPermissions,
            })
          );
    },
    [currentOrganizationPermissions]
  );

  return can;
};
